import { useState, useEffect, useCallback, useRef } from 'react';
import { useRecoilValue } from 'recoil';
import { selectedSiteAtom } from '../state/sitesAtom';
import { zonesApi } from '../api';

/**
 * Hook for fetching zones list
 */
export function useZones(filters) {
  const selectedSite = useRecoilValue(selectedSiteAtom);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Serialize filters for stable dependency
  const filtersKey = JSON.stringify(filters || {});
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        site: selectedSite !== 'all' ? selectedSite : undefined,
        ...(filtersRef.current || {}),
      };

      const result = await zonesApi.getAll(params);
      setData(result.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSite, filtersKey]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

/**
 * Hook for fetching a single zone
 */
export function useZone(id) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await zonesApi.getById(id);
      setData(result.data || result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

/**
 * Hook for zone mutations
 */
export function useZoneMutations() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const createZone = useCallback(async (data) => {
    setLoading(true);
    setError(null);
    try {
      const result = await zonesApi.create(data);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateZone = useCallback(async (id, data) => {
    setLoading(true);
    setError(null);
    try {
      const result = await zonesApi.update(id, data);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteZone = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const result = await zonesApi.delete(id);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    createZone,
    updateZone,
    deleteZone,
    loading,
    error,
  };
}

export default useZones;
